import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import {
  Briefcase,
  ArrowUpRight,
  Check,
  Users,
  Zap,
  Lightbulb,
  Clock,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Globe,
} from 'lucide-react';
import { useState } from 'react';

const features = [
  { icon: Users, title: 'Free to start', text: 'One agent, core chat and ticket features, no setup fees.' },
  { icon: Zap, title: 'All channels', text: 'WhatsApp, Instagram, Telegram, email, and live chat in one place.' },
  { icon: Lightbulb, title: 'AI included', text: 'Zyra AI drafts replies and coaches agents on Pro.' },
  { icon: Clock, title: 'No annual lock-in', text: 'Add or remove agents monthly as you grow.' },
];

const benefits = [
  { icon: Users, title: '1 free agent', description: 'Start with one free agent forever, no time limit.' },
  { icon: TrendingUp, title: '$25 per agent', description: 'Transparent pricing with no hidden fees or add-ons.' },
  { icon: Clock, title: 'Minutes to setup', description: 'Create a workspace and start supporting customers in minutes.' },
  { icon: Globe, title: 'All channels included', description: 'WhatsApp, Instagram, email, and chat from day one.' },
];

const useCases = [
  { title: 'Online stores', text: 'Answer product and order questions where customers ask.' },
  { title: 'Service providers', text: 'Book appointments, answer quotes, and close faster.' },
  { title: 'SaaS founders', text: 'Support early users without engineering overhead.' },
  { title: 'Local businesses', text: 'Manage inquiries and bookings from multiple channels.' },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'Is there a free plan?',
    answer: 'Yes. The free plan supports one agent with core chat and ticket features.',
  },
  {
    question: 'Can I add more agents later?',
    answer: 'Yes. Add or remove Pro agents at any time.',
  },
  {
    question: 'What channels are included?',
    answer: 'WhatsApp, Telegram, Instagram, email, live chat, and Facebook Messenger are available.',
  },
  {
    question: 'Is AI included in the free plan?',
    answer: 'AI features are available on the Pro plan at $25 per agent per month.',
  },
];

function FAQAccordion({ faqs }: { faqs: Array<{ question: string; answer: string }> }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="flex w-full items-center justify-between p-5 text-left"
            aria-expanded={openIndex === index}
          >
            <span className="pr-4 font-semibold text-slate-900">{faq.question}</span>
            {openIndex === index ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-slate-500" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" />
            )}
          </button>
          {openIndex === index && (
            <div className="px-5 pb-5">
              <p className="text-slate-600">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ZuriHubForSmallBusinessesPage() {
  return (
    <>
      <SEO
        title="ZuriHub for Small Businesses - Free Support Platform | ZuriHub"
        description="Start free and scale. One platform for live chat, tickets, WhatsApp, AI, and analytics. Free agent plan with transparent $25 per agent pricing."
        path="/zurihub-for-small-businesses"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Briefcase className="h-4 w-4" /> Built for Growth
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                One platform that <span className="gradient-text">grows with your team</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Small teams don't have time for five different tools. One inbox, one AI assistant, one knowledge base, and one analytics view — starting with a free agent plan.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/book-a-demo"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  Book a demo <Briefcase className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/hero_business_startups__en_us.avif"
                  alt="ZuriHub for Small Businesses showing unified inbox and AI features"
                  className="rounded-3xl shadow-2xl shadow-brand-900/30"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Features small businesses love</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to support customers, no bloat.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why small businesses choose ZuriHub</h2>
            <p className="section-subtitle mx-auto mt-4">Benefits that help you grow without breaking the bank.</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <benefit.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{benefit.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="usecases-heading" className="text-center section-title">Use cases</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="how-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">How it works</h2>
              <p className="mt-4 text-slate-300">
                Create your workspace, connect channels, train Zyra AI, and start supporting customers. Most small teams are up and running in an hour.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  {
                    icon: Briefcase,
                    title: 'Create workspace',
                    description: 'Sign up and invite your team in under five minutes.',
                  },
                  {
                    icon: Zap,
                    title: 'Connect channels',
                    description: 'Add live chat, WhatsApp, Instagram, or email.',
                  },
                  {
                    icon: Lightbulb,
                    title: 'Train Zyra AI',
                    description: 'Point Zyra at your help articles and common replies.',
                  },
                  {
                    icon: Globe,
                    title: 'Start supporting',
                    description: 'Reply faster, close more tickets, and grow with data.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="font-bold">Small business features</h3>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Free to start</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> All channels included</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> AI assistant</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Knowledge base</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Analytics</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> No annual lock-in</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Start free today</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Create your workspace and chat with customers in minutes.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Create your workspace <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about small business</p>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>
    </>
  );
}
